import { navigationDrawerStyles as styles } from "@iewcasia/Overrides/Components/NavigationDrawer";
import { headingCommon } from "@iewcasia/Utilities/styleguide/Typography";
import { colorStrings, fontFamilyHeader, mediaQueriesBP } from "@iewcasia/Utilities/variables";
import baseTheme, { ThemeTypography } from "@insite/mobius/globals/baseTheme";
import getColor from "@insite/mobius/utilities/getColor";
import { css } from "styled-components";

const mainNavigationRowTypography = css`
    ${baseTheme.typography.h3.css}
    display:inline-flex;
    font-family: ${fontFamilyHeader};
    font-size: 21px;
    line-height: 27px;
    text-transform: uppercase;
    margin-bottom: 0;
    color: ${getColor(colorStrings.textMain)};

    ${mediaQueriesBP.maxMD} {
        font-size: 21px;
        line-height: 27px;
    }
`;

const commonButtonStylesMobile = css`
    padding-block: 0;
    min-height: 60px;
    background: transparent !important;
    color: ${getColor(colorStrings.textMain)};

    &&&& span {
        font-size: 18px;
    }
`;

styles.menuTriggerButton = {
    ...styles.menuTriggerButton,
    size: 50,
    css: css`
        color: ${getColor(colorStrings.textMain)};
        background: transparent;
        border: none;

        svg {
            color: ${getColor(colorStrings.textMain)};
        }
    `,
};

styles.logoLinks = {
    typographyProps: {
        variant: "h5",
        mergeCss: true,
        color: colorStrings.textMain,
        ellipsis: true,
    },
    css: css`
        margin: 2px;
        background: transparent !important;
        border-top: 1px solid ${getColor(colorStrings.commonBorder)};

        &:last-child {
            border-bottom: 1px solid ${getColor(colorStrings.commonBorder)};
        }

        &.logo-links-row {
            ${commonButtonStylesMobile};
            color: ${getColor(colorStrings.commonGlobalGray02)};
        }

        &.logo-links-row__heading {
            ${mainNavigationRowTypography};
            color: ${getColor(colorStrings.textMain)};
        }
    `,
};

styles.drawer = {
    ...styles.drawer,
    cssOverrides: {
        drawerTitle: css`
            background: ${getColor(colorStrings.commonGlobalGray05)};
            color: ${getColor(colorStrings.textMain)};
            margin: 0;
            padding: 0;
        `,
        drawerBody: css`
            width: 100%;
            transform: translate(-100%, 0);
            background: ${getColor(colorStrings.commonGlobalWhite)};

            ${mediaQueriesBP.minMD} {
                margin-top: 100px;
            }
        `,
    },
    closeButtonProps: {
        css: css`
            background: transparent;
            border: none;
            color: ${getColor(colorStrings.textMain)};

            svg {
                color: ${getColor(colorStrings.textMain)};
            }
        `,
    },
};

styles.drawerSectionWrapper = {
    ...styles.drawerSectionWrapper,
    css: css`
        display: flex;
        flex-direction: column;

        &:first-child {
            margin-top: 0;
            padding: 0;
            background: transparent;
            color: ${getColor(colorStrings.textMain)};
        }

        &:last-child {
            margin-bottom: 5px;
        }
    `,
};

styles.mainNavigationRow = {
    ...styles.mainNavigationRow,
    css: css`
        border-top: 1px solid ${getColor(colorStrings.commonBorder)};
        margin-top: -1px;
        font-size: 18px;
        ${commonButtonStylesMobile};

        svg {
            color: ${getColor(colorStrings.textMain)};
        }
    `,
};

styles.mainNavigationRowIcon = {
    ...styles.mainNavigationRowIcon,
    size: 16,
    css: css`
        color: ${getColor(colorStrings.textMain)};
        margin-right: 10px;

        svg {
            color: ${getColor(colorStrings.textMain)};
        }
    `,
};

styles.mainNavigationRowTypography = {
    ...styles.mainNavigationRowTypography,
    color: colorStrings.textMain,
    variant: "h5",
    css: css`
        ${mainNavigationRowTypography};
        color: ${getColor(colorStrings.textMain)};
        margin: 0;
    `,
};

styles.userRowTypography = {
    ...styles.userRowTypography,
    color: colorStrings.textMain,
};

styles.panelMenu = {
    ...styles.panelMenu,
    cssOverrides: {
        wrapper: css`
            background: transparent !important;
            color: ${getColor(colorStrings.textMain)};
            border-top: 1px solid ${getColor(colorStrings.commonBorder)};

            > button {
                width: calc(100% - 2px);
                text-align: left;
                ${commonButtonStylesMobile};
            }

            > a {
                ${commonButtonStylesMobile};
            }
        `,
        children: css`
            background: transparent !important;
            color: ${getColor(colorStrings.textMain)};

            > a {
                color: ${getColor(colorStrings.textMain)};
                background: transparent !important;
            }
        `,
    },
    childTypographyProps: {
        mergeCss: true,
        color: colorStrings.textMain,
        ellipsis: true,
        size: 18,
        css: css`
            background: transparent;
            color: ${getColor(colorStrings.textMain)};
        `,
    },
    bodyColor: colorStrings.commonGlobalWhite,
    headerColor: colorStrings.commonGlobalGray05,
    panelRowProps: {
        css: css`
            ${commonButtonStylesMobile};
            border-top: 1px solid ${getColor(colorStrings.commonBorder)};
        `,
    },
    accordionSectionProps: {
        collapseIconProps: {
            src: "Minus",
            css: css`
                color: ${getColor(colorStrings.textMain)};
            `,
        },
        expandIconProps: {
            src: "Plus",
            css: css`
                color: ${getColor(colorStrings.textMain)};
            `,
        },
        headerProps: {
            color: colorStrings.textMain,
            css: css`
                button {
                    padding: 0 20px;
                    border: 0;
                    border-top: 1px solid ${getColor(colorStrings.commonBorder)};
                    ${commonButtonStylesMobile};

                    a {
                        display: flex;
                        align-items: center;
                        margin: -1px 0 0;
                    }

                    &:focus {
                        padding: 0 20px;
                    }
                }

                &&&& span {
                    color: ${getColor(colorStrings.textMain)};
                    font-size: 18px;
                    font-weight: 400;
                }
            `,
        },
        panelProps: {
            css: css`
                dd {
                    border: 0;
                    padding: 0;
                }

                &&&& span {
                    color: ${getColor(colorStrings.textMain)};
                    font-size: 16px;
                    margin: 5px 0;
                    padding: 0 10px;
                }
            `,
        },
    },

    mergeCss: true,
};

styles.userRowTypography = {
    ...styles.userRowTypography,
    color: colorStrings.primaryMain,
    transform: "none",
};
